import { useMemo, useState, type JSX } from 'react'
import { Link } from 'react-router'
import { Chart } from 'primereact/chart'
import {
  MessagesSquare,
  CheckCircle2,
  Timer,
  Gauge,
  ShieldAlert,
  Smile,
  TrendingUp,
  Flag,
  ArrowRight,
  Folders,
  Tags,
  FolderTree,
} from 'lucide-react'
import { CHANNEL_META, sourceMeta, type ChannelMeta } from '../../components/conversations/channelMeta.js'
import { useConversationDashboard, type RecentConversation } from '../../hooks/useConversationDashboard.js'
import { timestampMs } from '../../utils/timezone.js'
import DatePicker from '../../components/ui/DatePicker.js'

/* ───────────────────────────────────────────────
   Elegant, colorful design tokens
   ─────────────────────────────────────────────── */

const CARD =
  'rounded-[3px] bg-white ring-1 ring-slate-900/[0.05] shadow-[0_1px_2px_rgba(15,23,42,0.03),0_12px_30px_-14px_rgba(15,23,42,0.12)]'
const CARD_HOVER = 'transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-16px_rgba(15,23,42,0.18)]'
const BAR_TRACK = 'h-1.5 w-full overflow-hidden rounded-full bg-slate-100'
const DOT = 'h-1.5 w-1.5 rounded-full'

/** Bar/segment hues per channel — icon + label come from the shared
 *  channelMeta registry so channels look identical to the inbox. */
const CHANNEL_HEX: Record<string, string> = {
  email: '#C026D3',
  sms: '#16A34A',
  whatsapp: '#059669',
  facebook: '#1D4ED8',
  instagram: '#DB2777',
  chat: '#7C3AED',
  web: '#4F46E5',
  voice: '#EA580C',
  youtube: '#DC2626',
  x: '#0284C7',
  telegram: '#0EA5E9',
}

interface ChannelRowMeta {
  label: string
  Icon: ChannelMeta['icon']
  bg: string
  fg: string
  hex: string
}

function channelMeta(code: string): ChannelRowMeta {
  const key = code.toLowerCase()
  const shared = CHANNEL_META[key] ?? sourceMeta(key, undefined)
  const label = key === 'chat' ? 'Live Chat' : shared.label
  return { label, Icon: shared.icon, bg: shared.bg, fg: shared.fg, hex: CHANNEL_HEX[key] ?? '#64748B' }
}

const STATUS_META: Record<string, { label: string; hex: string; soft: string; text: string; tag: string }> = {
  open: { label: 'Open', hex: '#2563EB', soft: 'bg-blue-50', text: 'text-blue-700', tag: 'Active' },
  closed: { label: 'Closed', hex: '#10B981', soft: 'bg-emerald-50', text: 'text-emerald-700', tag: 'Resolved' },
  on_hold: { label: 'On hold', hex: '#F59E0B', soft: 'bg-amber-50', text: 'text-amber-700', tag: 'Pending' },
  reopen: { label: 'Reopened', hex: '#E11D48', soft: 'bg-rose-50', text: 'text-rose-700', tag: 'Review' },
}

/* ───────────────────────────────────────────────
   Building blocks
   ─────────────────────────────────────────────── */

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <div className={`${CARD} ${className}`}>{children}</div>
}

function CardHead({ icon, tint, title, subtitle, action }: { icon: JSX.Element; tint: string; title: string; subtitle?: string; action?: JSX.Element }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 items-center justify-center rounded-[3px] ${tint}`}>{icon}</span>
        <div>
          <h2 className="text-sm font-semibold leading-tight tracking-tight text-[#0f172a]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[11.5px] leading-tight text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  )
}

/** KPI tile — same card language as the My Dashboard status tiles:
 *  dot + uppercase label + tag/icon on top, mono value + right stat,
 *  optional progress bar and hint line. */
function KpiCard({
  label,
  hex,
  tag,
  tagCls = 'bg-slate-100 text-slate-600 ring-slate-200',
  value,
  unit,
  right,
  hint,
  icon,
  tint,
}: {
  label: string
  hex: string
  tag?: string | undefined
  tagCls?: string
  value: string | number
  unit?: string
  right?: string | undefined
  hint?: string | undefined
  icon?: JSX.Element | undefined
  tint?: string | undefined
}): JSX.Element {
  return (
    <div className={`${CARD} ${CARD_HOVER} p-4`}>
      <div className="flex items-center justify-between">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: hex }} />
          <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
        </span>
        {tag !== undefined ? (
          <span className={`inline-flex shrink-0 items-center rounded-[3px] px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${tagCls}`}>
            {tag}
          </span>
        ) : icon !== undefined && tint !== undefined ? (
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] ${tint}`}>{icon}</span>
        ) : null}
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className={`font-mono text-3xl font-extrabold tabular-nums leading-none tracking-tight ${value === '—' ? 'text-slate-400' : 'text-[#0f172a]'}`}>
          {value}
          {unit !== undefined && <span className="ml-1 text-[13px] font-medium text-slate-400">{unit}</span>}
        </span>
        {right !== undefined && (
          <span className="font-mono text-[11px] font-medium tabular-nums text-slate-500">{right}</span>
        )}
      </div>
      {hint !== undefined && <p className="mt-2 truncate text-[11.5px] text-slate-400">{hint}</p>}
    </div>
  )
}

/* ───────────────────────────────────────────────
   Skeleton
   ─────────────────────────────────────────────── */

function Skeleton(): JSX.Element {
  return (
    <div role="status" aria-label="Loading dashboard" className="min-h-full bg-slate-50 p-6">
      <div className="mb-8 h-9 w-72 animate-pulse rounded-[3px] bg-slate-200" />
      <div className="mb-7 grid grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={`h-[136px] animate-pulse ${CARD}`} />
        ))}
      </div>
      <div className="mb-7 grid grid-cols-3 gap-5">
        <div className={`col-span-2 h-80 animate-pulse ${CARD}`} />
        <div className={`h-80 animate-pulse ${CARD}`} />
      </div>
      <div className={`h-96 animate-pulse ${CARD}`} />
    </div>
  )
}

/* ───────────────────────────────────────────────
   Helpers
   ─────────────────────────────────────────────── */

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const then = timestampMs(iso)
  if (isNaN(then)) return '—'
  const diff = Math.max(0, Date.now() - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function statusPill(status: string): JSX.Element {
  const meta = STATUS_META[status]
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[3px] px-2.5 py-1 text-[11px] font-semibold ${meta?.soft ?? 'bg-slate-100'} ${meta?.text ?? 'text-slate-600'}`}>
      <span className={DOT} style={{ backgroundColor: meta?.hex ?? '#94a3b8' }} />
      {meta?.label ?? status}
    </span>
  )
}

/* ───────────────────────────────────────────────
   Recent conversations table
   ─────────────────────────────────────────────── */

function RecentTable({ rows }: { rows: RecentConversation[] }): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[3px] bg-gradient-to-br from-slate-100 to-slate-50 ring-1 ring-slate-200/60">
          <Folders className="h-6 w-6 text-slate-300" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-semibold text-slate-500">No conversations in this range</p>
        <p className="mt-1 text-xs text-slate-400">Adjust the date range to see activity</p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-[13px]">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <th className="px-6 py-3 font-semibold">Reference</th>
            <th className="px-4 py-3 font-semibold">Contact</th>
            <th className="px-4 py-3 font-semibold">Channel</th>
            <th className="px-4 py-3 font-semibold">Priority</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">SLA</th>
            <th className="px-6 py-3 text-right font-semibold">Last activity</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => {
            const ch = channelMeta(r.channel)
            return (
              <tr key={r.referenceId} className="transition-colors hover:bg-indigo-50/30">
                <td className="px-6 py-3.5">
                  <div className="font-mono text-[12px] font-semibold text-slate-800">{r.referenceId}</div>
                  {r.subject && <div className="mt-0.5 max-w-[240px] truncate text-[11.5px] text-slate-400">{r.subject}</div>}
                </td>
                <td className="px-4 py-3.5 text-slate-600">{r.displayName || '—'}</td>
                <td className="px-4 py-3.5">
                  <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-600">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-[3px] ${ch.bg} ${ch.fg}`}>
                      <ch.Icon className="h-3.5 w-3.5" />
                    </span>
                    {ch.label}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  {r.priority ? (
                    <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-600">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.priorityColor || '#94a3b8' }} />
                      {r.priority}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3.5">{statusPill(r.status)}</td>
                <td className="px-4 py-3.5">
                  {r.slaBreached ? (
                    <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-600">
                      <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2} /> Breached
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> On track
                    </span>
                  )}
                </td>
                <td className="px-6 py-3.5 text-right text-[12.5px] text-slate-400">{timeAgo(r.lastMessageAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* Category/subcategory bars — stable palette by rank */
const CAT_PALETTE = ['#6366f1', '#14b8a6', '#f59e0b', '#f43f5e', '#8b5cf6', '#0ea5e9', '#84cc16', '#f97316']

function catColor(i: number): string {
  return CAT_PALETTE[i % CAT_PALETTE.length]!
}

function RankBars({ rows, total }: { rows: { label: string; count: number }[]; total: number }): JSX.Element {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div className="space-y-4">
      {rows.slice(0, 8).map((r, i) => (
        <div key={r.label}>
          <div className="mb-2 flex items-center justify-between">
            <span className="inline-flex items-center gap-2.5 text-[12.5px] font-medium text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: catColor(i) }} />
              {r.label}
            </span>
            <span className="inline-flex items-baseline gap-1.5 text-[12.5px] font-semibold text-slate-800 tabular-nums">
              {r.count}
              <span className="font-normal text-slate-400">{total > 0 ? Math.round((r.count / total) * 100) : 0}%</span>
            </span>
          </div>
          <div className={BAR_TRACK}>
            <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: catColor(i) }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ───────────────────────────────────────────────
   Page
   ─────────────────────────────────────────────── */

const ALL_STATUSES = ['open', 'on_hold', 'closed', 'reopen'] as const

export default function ConversationDashboardPage(): JSX.Element {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  // Status tiles double as the filter: deselecting one removes that status
  // from every widget on the page (total, charts, recent list).
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([...ALL_STATUSES])
  const toggleStatus = (k: string): void => {
    setSelectedStatuses((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]))
  }
  const { data, loading } = useConversationDashboard(fromDate, toDate, selectedStatuses)

  const channelRows = useMemo(
    () =>
      Object.entries(data.channelCounts)
        .map(([code, count]) => ({ code, count, ...channelMeta(code) }))
        .sort((a, b) => b.count - a.count),
    [data.channelCounts],
  )
  const maxChannel = Math.max(1, ...channelRows.map((c) => c.count))
  const maxPriority = Math.max(1, ...data.priorityCounts.map((p) => p.count))
  const resolvedTotal = useMemo(() => data.trend.reduce((s, t) => s + t.resolved, 0), [data.trend])

  /* Status donut — slices mirror the active status filter */
  const donutData = useMemo(() => {
    const keys: (keyof typeof data.statusCounts)[] = ['open', 'closed', 'on_hold', 'reopen']
    const present = keys.filter((k) => selectedStatuses.includes(k) && data.statusCounts[k] > 0)
    return {
      labels: present.map((k) => STATUS_META[k]!.label),
      datasets: [
        {
          data: present.map((k) => data.statusCounts[k]),
          backgroundColor: present.map((k) => STATUS_META[k]!.hex),
          borderColor: '#ffffff',
          borderWidth: 3,
          hoverOffset: 6,
        },
      ],
    }
  }, [data.statusCounts, selectedStatuses])

  const donutOptions = useMemo(
    () => ({
      cutout: '70%',
      plugins: {
        legend: { display: false },
        tooltip: { padding: 10, backgroundColor: '#0f172a', cornerRadius: 6, boxPadding: 4 },
      },
      maintainAspectRatio: false,
    }),
    [],
  )

  /* Trend — grouped bars */
  const fewDays = data.trend.length <= 2
  const trendData = useMemo(
    () => ({
      labels: data.trend.map((t) => t.day.slice(5)),
      datasets: [
        {
          label: 'Opened',
          data: data.trend.map((t) => t.opened),
          backgroundColor: '#6366f1',
          hoverBackgroundColor: '#4f46e5',
          borderRadius: 5,
          borderSkipped: false,
          maxBarThickness: fewDays ? 52 : 22,
          categoryPercentage: 0.6,
          barPercentage: 0.9,
        },
        {
          label: 'Resolved',
          data: data.trend.map((t) => t.resolved),
          backgroundColor: '#34d399',
          hoverBackgroundColor: '#10b981',
          borderRadius: 5,
          borderSkipped: false,
          maxBarThickness: fewDays ? 52 : 22,
          categoryPercentage: 0.6,
          barPercentage: 0.9,
        },
      ],
    }),
    [data.trend, fewDays],
  )

  const trendOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: { padding: 10, backgroundColor: '#0f172a', cornerRadius: 6, boxPadding: 4, usePointStyle: true },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', maxRotation: 0, autoSkipPadding: 16 }, border: { display: false } },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(148,163,184,0.14)' },
          ticks: { font: { size: 11 }, color: '#94a3b8', precision: 0, maxTicksLimit: 5 },
          border: { display: false },
        },
      },
    }),
    [],
  )

  if (loading && data.total === 0 && data.trend.length === 0) return <Skeleton />

  return (
    <div className="min-h-full bg-slate-50 p-8">
      {/* ── Header ── */}
      <section className={`${CARD} mb-7 px-5 py-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-[#0f172a]">Conversation Dashboard</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20">
                <MessagesSquare className="h-3 w-3" />
                Omnichannel
              </span>
            </div>
            <p className="flex flex-wrap items-center gap-x-1.5 text-[12px] text-slate-500">
              <span>Analytics across every conversation source</span>
              <span className="text-slate-300">•</span>
              <span>{fromDate && toDate ? `${fromDate} → ${toDate}` : 'All time'}</span>
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <DatePicker
              ariaLabel="From date"
              label="From"
              value={fromDate}
              onChange={setFromDate}
            />
            <DatePicker
              ariaLabel="To date"
              label="To"
              value={toDate}
              onChange={setToDate}
            />
          </div>
        </div>
      </section>

      {/* ── KPI row ── */}
      <section className="mb-7 grid grid-cols-2 gap-6 lg:grid-cols-4">
        <KpiCard
          label="Total"
          hex="#4F46E5"
          icon={<MessagesSquare className="h-[18px] w-[18px]" strokeWidth={2} />}
          tint="bg-indigo-50 text-indigo-600"
          value={data.total.toLocaleString()}
          right={`${data.unread} unread`}
          hint={`${data.starred} starred`}
        />
        <KpiCard
          label="SLA Compliance"
          hex={data.sla.tracked === 0 ? '#94A3B8' : data.sla.compliancePct >= 90 ? '#10B981' : data.sla.compliancePct >= 70 ? '#F59E0B' : '#E11D48'}
          tag={data.sla.tracked === 0 ? undefined : data.sla.compliancePct >= 90 ? 'On target' : data.sla.compliancePct >= 70 ? 'Watch' : 'Below'}
          tagCls={data.sla.compliancePct >= 90 ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : data.sla.compliancePct >= 70 ? 'bg-amber-50 text-amber-700 ring-amber-600/20' : 'bg-rose-50 text-rose-700 ring-rose-600/20'}
          icon={<Gauge className="h-[18px] w-[18px]" strokeWidth={2} />}
          tint="bg-slate-100 text-slate-500"
          value={data.sla.tracked > 0 ? `${data.sla.compliancePct}%` : '—'}
          right={`${data.sla.met} of ${data.sla.tracked}`}
          hint="conversations within their deadline"
        />
        <KpiCard
          label="Avg Response"
          hex="#0EA5E9"
          icon={<Timer className="h-[18px] w-[18px]" strokeWidth={2} />}
          tint="bg-sky-50 text-sky-600"
          value={data.sla.avgResponseHours}
          unit="hrs"
          right={`${data.sla.avgResolutionHours} hrs to resolve`}
          hint="average time to first reply"
        />
        <KpiCard
          label="CSAT"
          hex="#F59E0B"
          icon={<Smile className="h-[18px] w-[18px]" strokeWidth={2} />}
          tint="bg-amber-50 text-amber-600"
          value={data.csat.count > 0 ? data.csat.avg.toFixed(1) : '—'}
          unit={data.csat.count > 0 ? '/ 5' : ''}
          right={`${data.csat.count} responses`}
          hint="customer satisfaction score"
        />
      </section>

      {/* ── Status count strip — click a tile to include/exclude that status ── */}
      <section className="mb-7">
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {ALL_STATUSES.map((k) => {
            const meta = STATUS_META[k]!
            const selected = selectedStatuses.includes(k)
            const pct = selected && data.total > 0 ? (data.statusCounts[k] / data.total) * 100 : 0
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleStatus(k)}
                aria-pressed={selected}
                title={selected ? `Exclude ${meta.label} from the dashboard` : `Include ${meta.label} in the dashboard`}
                className={`${CARD} ${CARD_HOVER} p-4 text-left ${selected ? '' : 'opacity-50'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: meta.hex }} />
                    <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      {meta.label}
                    </span>
                  </span>
                  <span className={`inline-flex shrink-0 items-center rounded-[3px] px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ring-black/5 ${meta.soft} ${meta.text}`}>
                    {meta.tag}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="font-mono text-3xl font-extrabold tabular-nums tracking-tight text-[#0f172a]">
                    {data.statusCounts[k]}
                  </span>
                  <span className="font-mono text-[11px] font-medium tabular-nums text-slate-500">
                    {Math.round(pct)}% of all
                  </span>
                </div>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(pct, 100)}%`, background: meta.hex }}
                  />
                </div>
              </button>
            )
          })}
        </div>
        {selectedStatuses.length < ALL_STATUSES.length && (
          <p className="mt-2 text-[11.5px] font-medium text-slate-400">
            Showing {selectedStatuses.length === 0 ? 'no' : selectedStatuses.length} of {ALL_STATUSES.length} statuses — click a tile to toggle it back on.
          </p>
        )}
      </section>

      {/* ── Trend + Status donut ── */}
      <section className="mb-7 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHead
            icon={<TrendingUp className="h-4 w-4" strokeWidth={2} />}
            tint="bg-indigo-50 text-indigo-600"
            title="Conversation Trend"
            subtitle="Opened vs resolved per day"
            action={
              <div className="flex items-center gap-4 text-[11.5px] font-medium text-slate-500">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-indigo-500" /> Opened</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Resolved</span>
              </div>
            }
          />
          <div className="h-64 px-4 pb-5 pt-5">
            {data.trend.length > 0 ? (
              <Chart type="bar" data={trendData} options={trendOptions} className="h-full w-full" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
                <TrendingUp className="h-7 w-7 text-slate-300" strokeWidth={1.5} />
                <span className="text-sm font-medium">No activity in this range</span>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHead icon={<MessagesSquare className="h-4 w-4" strokeWidth={2} />} tint="bg-violet-50 text-violet-600" title="Status Split" subtitle={`${data.total} total`} />
          {data.total > 0 ? (
            <div className="px-6 pb-6 pt-5">
              <div className="relative mx-auto h-40 w-40">
                <Chart type="doughnut" data={donutData} options={donutOptions} className="h-full w-full" />
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[26px] font-bold leading-none text-slate-900 tabular-nums">{data.total}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">total</span>
                </div>
              </div>
              <div className="mt-6 space-y-2.5">
                {(['open', 'closed', 'on_hold', 'reopen'] as const).filter((k) => selectedStatuses.includes(k)).map((k) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-[12px] text-slate-600">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_META[k]!.hex }} />
                      {STATUS_META[k]!.label}
                    </span>
                    <span className="text-[12px] font-semibold text-slate-800 tabular-nums">{data.statusCounts[k]}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-56 items-center justify-center text-sm text-slate-400">No conversations</div>
          )}
        </Card>
      </section>

      {/* ── Channel + Priority breakdown ── */}
      <section className="mb-7 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHead icon={<MessagesSquare className="h-4 w-4" strokeWidth={2} />} tint="bg-blue-50 text-blue-600" title="Channel Breakdown" subtitle="Volume by source channel" />
          <div className="px-6 pb-6 pt-5">
            {channelRows.length > 0 ? (
              <div className="space-y-4">
                {channelRows.map((c) => (
                  <div key={c.code}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2.5 text-[12.5px] font-medium text-slate-600">
                        <span className={`flex h-6 w-6 items-center justify-center rounded-[3px] ${c.bg} ${c.fg}`}><c.Icon className="h-3.5 w-3.5" /></span>
                        {c.label}
                      </span>
                      <span className="inline-flex items-baseline gap-1.5 text-[12.5px] font-semibold text-slate-800 tabular-nums">
                        {c.count}
                        <span className="font-normal text-slate-400">{data.total > 0 ? Math.round((c.count / data.total) * 100) : 0}%</span>
                      </span>
                    </div>
                    <div className={BAR_TRACK}>
                      <div className="h-full rounded-full" style={{ width: `${(c.count / maxChannel) * 100}%`, background: c.hex }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-slate-400">No channel data</div>
            )}
          </div>
        </Card>

        <Card>
          <CardHead icon={<Flag className="h-4 w-4" strokeWidth={2} />} tint="bg-rose-50 text-rose-600" title="Priority Breakdown" subtitle="Distribution by priority level" />
          <div className="px-6 pb-6 pt-5">
            {data.priorityCounts.length > 0 ? (
              <div className="space-y-4">
                {data.priorityCounts.map((p) => (
                  <div key={p.label}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2.5 text-[12.5px] font-medium text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.label}
                      </span>
                      <span className="inline-flex items-baseline gap-1.5 text-[12.5px] font-semibold text-slate-800 tabular-nums">
                        {p.count}
                        <span className="font-normal text-slate-400">{data.total > 0 ? Math.round((p.count / data.total) * 100) : 0}%</span>
                      </span>
                    </div>
                    <div className={BAR_TRACK}>
                      <div className="h-full rounded-full" style={{ width: `${(p.count / maxPriority) * 100}%`, background: p.color }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-slate-400">No priority data</div>
            )}
          </div>
        </Card>
      </section>

      {/* ── Category + Subcategory breakdown ── */}
      <section className="mb-7 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHead icon={<Tags className="h-4 w-4" strokeWidth={2} />} tint="bg-emerald-50 text-emerald-600" title="Categories" subtitle="Volume by conversation category" />
          <div className="px-6 pb-6 pt-5">
            {data.categoryCounts.length > 0 ? (
              <RankBars rows={data.categoryCounts} total={data.total} />
            ) : (
              <div className="py-10 text-center text-sm text-slate-400">No category data</div>
            )}
          </div>
        </Card>

        <Card>
          <CardHead icon={<FolderTree className="h-4 w-4" strokeWidth={2} />} tint="bg-violet-50 text-violet-600" title="Subcategories" subtitle="Most frequent issue types" />
          <div className="px-6 pb-6 pt-5">
            {data.subcategoryCounts.length > 0 ? (
              <RankBars rows={data.subcategoryCounts} total={data.total} />
            ) : (
              <div className="py-10 text-center text-sm text-slate-400">No subcategory data</div>
            )}
          </div>
        </Card>
      </section>

      {/* ── SLA snapshot ── */}
      <section className="mb-7 grid grid-cols-2 gap-6 lg:grid-cols-4">
        <KpiCard
          label="SLA Met"
          hex="#10B981"
          value={data.sla.met}
          right={`of ${data.sla.tracked} tracked`}
          hint="within their deadline"
        />
        <KpiCard
          label="SLA Breached"
          hex="#E11D48"
          tag={data.sla.breached > 0 ? 'Attention' : 'Clean'}
          tagCls={data.sla.breached > 0 ? 'bg-rose-50 text-rose-700 ring-rose-600/20' : 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'}
          value={data.sla.breached}
          right={`of ${data.sla.tracked} tracked`}
          hint="went past their deadline"
        />
        <KpiCard
          label="Resolved"
          hex="#0EA5E9"
          value={resolvedTotal}
          right={data.total > 0 ? `${Math.round((resolvedTotal / data.total) * 100)}% of all` : '—'}
          hint="in the selected range"
        />
        <KpiCard
          label="Archived"
          hex="#64748B"
          value={data.archived}
          right={data.total > 0 ? `${Math.round((data.archived / data.total) * 100)}% of all` : '—'}
          hint="out of the active view"
        />
      </section>

      {/* ── Recent conversations ── */}
      <Card className="overflow-hidden">
        <CardHead
          icon={<MessagesSquare className="h-[18px] w-[18px]" strokeWidth={2} />}
          tint="bg-indigo-50 text-indigo-600"
          title="Recent Conversations"
          subtitle="Latest activity across channels"
          action={
            <Link to="/agent/conversation/inbox?scope=all" className="inline-flex items-center gap-1 rounded-[3px] bg-indigo-50 px-3 py-1.5 text-[12px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-100">
              View all <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          }
        />
        <RecentTable rows={data.recent} />
      </Card>
    </div>
  )
}
